/**
 * The Contact section — `docs/new-request.md` §5.0, §5.4, §6; Boards 2, 3, 4,
 * 6, 14.
 *
 * Four shapes, one section:
 *
 * - **empty** — the heading says to find them in the search, and **Add a new
 *   contact** shows the seven fields (absent without `contacts ≥ edit`, §19 B3);
 * - **new** — the seven fields, with the amber *already here* band while a
 *   typed phone or email belongs to somebody (§6.3) — it never blocks;
 * - **chosen** — the card, with **Change**;
 * - **who at <property>?** — a property was chosen first: its people as radio
 *   rows, the primary chosen, and *Someone new at <property>* (§5.4).
 *
 * A typed contact that is ready becomes the same card with a **New** tag and
 * **Edit** once focus leaves the section (`mode: 'card'`).
 */
import { useEffect, useRef, useState } from 'react'
import {
  formatPhone, PHONE_KIND_LABELS, PHONE_KINDS, PREFERS_LABELS,
  type KnownContact, type PhoneKind, type Prefers,
} from '@rowted/shared'
import { cn } from '../lib/cn.js'
import { request } from '../lib/api.js'
import { Avatar, Button, Segmented } from '../components/primitives.js'
import { PlusGlyph } from '../components/keycap.js'
import { Field } from '../components/form/field.js'
import { TextInput } from '../components/form/inputs.js'
import { ComboInput, type ComboItem } from '../components/form/combo.js'
import { Popover } from '../components/overlays.js'
import { PanelSheet } from '../components/panel-sheet.js'
import { IconCall, IconCheck, IconChevronDown, IconCompany } from '../frame/icons.js'
import { requestsApi } from './api.js'
import { EMPTY_TYPED_CONTACT, type SheetState, type TypedContact } from './model.js'
import { MissingHint, ReplaceAsk, SectionHeading } from './section-frame.js'
import { KeySelect } from './key-select.js'
import { useRequestSheet } from './sheet-context.js'

// ---------------------------------------------------------------------------
// Words
// ---------------------------------------------------------------------------

/** *(717) 555-0164 · Mobile · dana.okafor@example.com · prefers Text* (Board 3). */
export function knownContactLine(contact: {
  phone: string | null; phoneKind: PhoneKind | null; email?: string | null; prefers: Prefers | null
}, withEmail = true): string {
  return [
    contact.phone,
    contact.phone && contact.phoneKind ? PHONE_KIND_LABELS[contact.phoneKind] : null,
    withEmail ? contact.email : null,
    contact.prefers ? `prefers ${PREFERS_LABELS[contact.prefers]}` : null,
  ].filter(Boolean).join(' · ')
}

function typedLine(fields: TypedContact): string {
  return knownContactLine({
    phone: fields.phone.trim() ? formatPhone(fields.phone) : null,
    phoneKind: fields.phoneKind,
    email: fields.email.trim() || null,
    prefers: fields.prefers,
  })
}

/** The first words of the folded line (§5.0): the name, else what was typed. */
export function contactSummary(state: SheetState): string {
  if (state.contact.kind === 'chosen') {
    return `${state.contact.contact.firstName} ${state.contact.contact.lastName}`
  }
  if (state.contact.kind === 'new') {
    const fields = state.contact.fields
    return [`${fields.firstName} ${fields.lastName}`.trim(), fields.company.trim(),
      fields.phone.trim() ? formatPhone(fields.phone) : '', fields.email.trim()]
      .filter(Boolean).join(' · ')
  }
  return ''
}

// ---------------------------------------------------------------------------
// The card (Boards 3, 6)
// ---------------------------------------------------------------------------

export function NewTag() {
  return (
    <span className="inline-flex h-[19px] items-center whitespace-nowrap rounded-tag bg-info-bg px-1.5 text-tag text-info">
      New
    </span>
  )
}

function ContactCard({ name, line, isNew, action, onAction }: {
  name: string
  line: string
  isNew: boolean
  action: 'Edit' | 'Change'
  onAction: () => void
}) {
  return (
    <div className="flex items-center gap-3 rounded-[12px] border border-accent-line bg-green-band-bg px-3.5 py-3">
      <Avatar name={name} size={38} />
      {/* Tight lines: the card is the avatar plus 12px either side, 64px (Board 5). */}
      <div className="flex min-w-0 flex-col gap-[3px] leading-tight">
        <div className="flex items-center gap-1.5">
          <b className="truncate text-[14.5px] font-bold text-text">{name}</b>
          {isNew && <NewTag />}
        </div>
        <span className="truncate text-[12.5px] text-text-muted">{line}</span>
      </div>
      <span className="flex-1" />
      <button type="button" onClick={onAction}
        className="h-[30px] shrink-0 rounded-button px-1.5 text-[12.5px] font-semibold text-accent hover:underline">
        {action}
      </button>
    </div>
  )
}

// ---------------------------------------------------------------------------
// The phone kind (§6.2, Board 4 C)
// ---------------------------------------------------------------------------

function KindRows({ value, onPick, tall }: {
  value: PhoneKind; onPick: (kind: PhoneKind) => void; tall?: boolean
}) {
  return (
    <div role="listbox" aria-label="Kind of number" className="py-1">
      {PHONE_KINDS.map((kind) => (
        <button
          key={kind}
          type="button"
          role="option"
          aria-selected={kind === value}
          onClick={() => onPick(kind)}
          className={cn('mx-1 flex w-[calc(100%-8px)] items-center gap-2.5 rounded-[8px] px-3 text-left',
            tall ? 'h-[52px] text-[15px]' : 'h-[38px] text-[13px]',
            kind === value ? 'bg-green-band-bg font-semibold text-accent-dark' : 'text-text hover:bg-hover')}
        >
          <span className={cn('inline-flex', kind === value ? 'text-accent' : 'text-text-muted')}>
            <IconCall size={15} />
          </span>
          {PHONE_KIND_LABELS[kind]}
          <span className="flex-1" />
          {kind === value && <span className="inline-flex text-accent"><IconCheck size={15} /></span>}
        </button>
      ))}
    </div>
  )
}

function PhoneField({ kind, number, onKind, onNumber, phone }: {
  kind: PhoneKind
  number: string
  onKind: (kind: PhoneKind) => void
  onNumber: (number: string) => void
  phone: boolean
}) {
  const [open, setOpen] = useState(false)
  const anchor = useRef<HTMLButtonElement>(null)
  return (
    <>
      <span className={cn('flex items-center overflow-hidden rounded-control border border-line bg-surface',
        'focus-within:border-accent-line focus-within:ring-2 focus-within:ring-accent-ring',
        phone ? 'h-11' : 'h-9')}>
        <button
          ref={anchor}
          type="button"
          aria-haspopup="listbox"
          aria-expanded={open}
          aria-label={`Kind of number: ${PHONE_KIND_LABELS[kind]}`}
          onClick={() => setOpen((was) => !was)}
          /*
           * The sheet's drop-downs all answer the keyboard one way (Aaron,
           * step 28): Tab in opens it, ↑ ↓ move, a first letter jumps, Enter or
           * Space takes it, Tab or Esc leaves.
           */
          onFocus={(event) => { if (!phone && event.currentTarget.matches(':focus-visible')) setOpen(true) }}
          onKeyDown={(event) => {
            if (phone) return
            const at = PHONE_KINDS.indexOf(kind)
            if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
              event.preventDefault()
              if (!open) { setOpen(true); return }
              const next = (at + (event.key === 'ArrowDown' ? 1 : PHONE_KINDS.length - 1)) % PHONE_KINDS.length
              onKind(PHONE_KINDS[next]!)
            } else if ((event.key === 'Enter' || event.key === ' ') && open) {
              event.preventDefault()
              setOpen(false)
            } else if (event.key === 'Tab' || event.key === 'Escape') {
              if (event.key === 'Escape' && open) event.stopPropagation()
              setOpen(false)
            } else if (/^[a-z]$/i.test(event.key)) {
              const hit = PHONE_KINDS.find((one) =>
                PHONE_KIND_LABELS[one].toLowerCase().startsWith(event.key.toLowerCase()))
              if (hit) { event.preventDefault(); onKind(hit) }
            }
          }}
          /*
           * A desktop's kind is a tinted segment with the phone glyph (Board 4);
           * a phone's is the word and a chevron inline, a short hairline after
           * it (Board 14 B).
           */
          className={cn('inline-flex h-full shrink-0 items-center gap-1.5 whitespace-nowrap font-semibold text-text',
            phone ? 'pl-3 pr-1.5 text-[13px]' : 'border-r border-line bg-surface-alt px-2.5 text-[12.5px]')}
        >
          {!phone && <span className="inline-flex text-text"><IconCall size={13} /></span>}
          {PHONE_KIND_LABELS[kind]}
          <span className="inline-flex text-text-muted"><IconChevronDown size={13} /></span>
        </button>
        {phone && <span aria-hidden className="h-5 w-px shrink-0 bg-line" />}
        <input
          data-request-field="phone"
          value={formatPhone(number)}
          inputMode="tel"
          autoComplete="tel"
          aria-label="Phone"
          /*
           * Formatted as it is typed — *(717) 555-0142*, Board 4 — the way the
           * contact form's `PhoneInput` does (walkthrough Oct 30, §10: it
           * showed *7175550142* until the field lost focus).
           */
          onChange={(event) => onNumber(formatPhone(event.target.value))}
          className={cn('min-w-0 flex-1 bg-transparent px-[11px] font-semibold text-text outline-none focus-visible:shadow-none',
            'placeholder:font-medium placeholder:text-text-faint', phone ? 'text-[16px]' : 'text-[13.5px]')}
        />
      </span>
      {!phone && (
        <Popover open={open} anchor={anchor.current} onClose={() => setOpen(false)} width={240}
          align="left" label="Kind of number">
          <KindRows value={kind} onPick={(next) => { onKind(next); setOpen(false) }} />
        </Popover>
      )}
      {phone && (
        <PanelSheet open={open} onClose={() => setOpen(false)} title="Kind of number"
          sub="Mobile is the number that gets texts">
          <KindRows tall value={kind} onPick={(next) => { onKind(next); setOpen(false) }} />
        </PanelSheet>
      )}
    </>
  )
}

// ---------------------------------------------------------------------------
// The seven fields (§6.1, Board 4)
// ---------------------------------------------------------------------------

interface Company { id: string; name: string; contactCount: number }

function useCompanies(): Company[] {
  const [companies, setCompanies] = useState<Company[]>([])
  useEffect(() => {
    request<{ items: Company[] }>('/contacts/companies')
      .then((result) => setCompanies(result.items ?? []))
      .catch(() => setCompanies([]))
  }, [])
  return companies
}

interface Duplicate { contactId: string; name: string; matchedOn: 'email' | 'phone' }

/** §6.3: asked 400 ms after the last key in Phone or Email. */
function useDuplicates(phoneText: string, email: string): Duplicate[] {
  const [found, setFound] = useState<Duplicate[]>([])
  const digits = phoneText.replace(/\D/g, '')
  const mail = email.trim().includes('@') ? email.trim() : ''
  useEffect(() => {
    if (digits.length < 7 && !mail) { setFound([]); return }
    let live = true
    const timer = window.setTimeout(() => {
      requestsApi.duplicates(digits.length >= 7 ? digits : null, mail || null)
        .then((answer) => { if (live) setFound(answer.matches) })
        .catch(() => { if (live) setFound([]) })
    }, 400)
    return () => { live = false; window.clearTimeout(timer) }
  }, [digits, mail])
  return found
}

function ContactFields({ fields, onChange, phone }: {
  fields: TypedContact
  onChange: (next: TypedContact) => void
  phone: boolean
}) {
  const api = useRequestSheet()
  const companies = useCompanies()
  const duplicates = useDuplicates(fields.phone, fields.email)
  const [dismissed, setDismissed] = useState<string | null>(null)
  const set = (change: Partial<TypedContact>) => onChange({ ...fields, ...change })
  const digits = fields.phone.replace(/\D/g, '')

  const byPhone = duplicates.find((one) => one.matchedOn === 'phone')
  const byEmail = duplicates.find((one) => one.matchedOn === 'email')
  const showPhoneBand = byPhone && dismissed !== digits && !fields.allowPhoneMatch
  const size = phone ? 'touch' as const : undefined

  const companyItem: ComboItem | null = fields.company.trim()
    ? { id: companies.find((one) => one.name === fields.company)?.id ?? `new:${fields.company}`,
      name: fields.company }
    : null

  return (
    <>
      <div className={cn('grid grid-cols-2', phone ? 'gap-2.5' : 'gap-x-3.5 gap-y-3')}>
        <Field label="First name" required>
          {({ id }) => (
            <>
              <TextInput id={id} data-request-field="firstName" value={fields.firstName}
                autoComplete="given-name" size={size}
                invalid={Boolean(api.hintFor('firstName'))}
                onChange={(event) => set({ firstName: event.target.value })} />
              <MissingHint text={api.hintFor('firstName')} />
            </>
          )}
        </Field>
        <Field label="Last name" required>
          {({ id }) => (
            <>
              <TextInput id={id} data-request-field="lastName" value={fields.lastName}
                autoComplete="family-name" size={size}
                invalid={Boolean(api.hintFor('lastName'))}
                onChange={(event) => set({ lastName: event.target.value })} />
              <MissingHint text={api.hintFor('lastName')} />
            </>
          )}
        </Field>
        <Field label="Company" optional={!phone}>
          {({ id, describedBy }) => (phone ? (
            <ComboInput
              id={id}
              describedBy={describedBy}
              label="Company"
              thing="company"
              size={phone ? 'touch' : 'default'}
              placeholder={phone ? 'optional' : ''}
              value={companyItem}
              onPick={(item) => set({ company: item.name })}
              search={(query) => companies
                .filter((one) => one.name.toLowerCase().includes(query.trim().toLowerCase()))
                .slice(0, 8)
                .map((one) => ({
                  id: one.id, name: one.name, lead: <IconCompany size={14} />,
                  sub: one.contactCount > 0
                    ? `${one.contactCount} ${one.contactCount === 1 ? 'person' : 'people'}`
                    : 'no people yet',
                }))}
              /* Made on Create, in the same transaction as everything else (§11.2 step 2). */
              onCreate={async (name) => ({ id: `new:${name}`, name: name.trim() })}
            />
          ) : (
            /* The sheet's one drop-down: open on Tab, type to filter, ↑ ↓, Enter or Space. */
            <KeySelect
              id={id}
              describedBy={describedBy}
              label="Company"
              placeholder=""
              value={companies.find((one) =>
                one.name.toLowerCase() === fields.company.trim().toLowerCase())?.id ?? null}
              display={fields.company.trim() || null}
              options={companies.map((one) => ({
                value: one.id, label: one.name, lead: <IconCompany size={14} />,
                sub: one.contactCount > 0
                  ? `${one.contactCount} ${one.contactCount === 1 ? 'person' : 'people'}`
                  : 'no people yet',
              }))}
              onChange={(picked) => set({ company: companies.find((one) => one.id === picked)?.name ?? '' })}
              onAdd={(text) => set({ company: text })}
              addWords={(text) => `New company “${text}”`}
            />
          ))}
        </Field>
        <Field label="Title" optional={!phone}>
          {({ id }) => (
            <TextInput id={id} value={fields.title} size={size}
              placeholder={phone ? 'optional' : undefined}
              onChange={(event) => set({ title: event.target.value })} />
          )}
        </Field>
        <Field label="Phone" group className={cn(phone && 'col-span-2')}>
          {() => (
            <PhoneField
              phone={phone}
              kind={fields.phoneKind}
              number={fields.phone}
              onKind={(phoneKind) => set({ phoneKind })}
              onNumber={(number) => set({ phone: number,
                allowPhoneMatch: number.replace(/\D/g, '') === digits ? fields.allowPhoneMatch : false })}
            />
          )}
        </Field>
        <Field label="Email" className={cn(phone && 'col-span-2')}>
          {({ id }) => (
            <>
              <TextInput id={id} data-request-field="email" value={fields.email} inputMode="email"
                autoComplete="email" size={size} invalid={Boolean(byEmail)}
                onChange={(event) => set({ email: event.target.value })} />
              {byEmail && (
                <span className="mt-1 block text-[11.5px] font-medium text-danger">
                  {byEmail.name} already has this email.{' '}
                  <button type="button" className="font-semibold text-accent hover:underline"
                    onClick={() => api.chooseContact(byEmail.contactId)}>
                    Use {byEmail.name}
                  </button>
                </span>
              )}
            </>
          )}
        </Field>
        <div className="col-span-2">
          <Field label="Prefers" group>
            {({ labelId }) => (
              /* Its own width on a desktop (Board 4); the whole row on a phone (Board 14). */
              <div className={phone ? undefined : 'self-start'}>
              <Segmented<Prefers>
                size={phone ? 'touch' : 'field'}
                full={phone}
                labelledBy={labelId}
                label="Prefers"
                value={fields.prefers}
                onChange={(prefers) => set({ prefers })}
                options={[
                  { value: 'sms', label: 'Text' },
                  { value: 'email', label: 'Email' },
                  { value: 'either', label: 'Either' },
                ]}
              />
              </div>
            )}
          </Field>
        </div>
      </div>

      {showPhoneBand && (
        <>
          <div className="mt-3 flex items-start gap-[9px] rounded-[10px] border border-amber-band-line bg-amber-band-bg px-3 py-2.5 text-[12.5px] leading-[1.45] text-amber-band-text">
            <div>
              <b>{byPhone.name} already has {formatPhone(fields.phone)}.</b>{' '}
              Same person? Use {byPhone.name} and the properties already on record instead of adding a second contact.
            </div>
          </div>
          <div className="mt-2 flex items-center gap-2">
            <Button className="h-8" onClick={() => api.chooseContact(byPhone.contactId)}>
              Use {byPhone.name}
            </Button>
            <button type="button"
              onClick={() => { setDismissed(digits); set({ allowPhoneMatch: true }) }}
              className="h-8 rounded-button px-2 text-[12.5px] font-semibold text-text-muted hover:text-text">
              It’s someone else
            </button>
          </div>
        </>
      )}
    </>
  )
}

// ---------------------------------------------------------------------------
// Who at <property>? (§5.4, Board 3 B)
// ---------------------------------------------------------------------------

function Radio({ on }: { on: boolean }) {
  return on
    ? <span className="inline-flex h-[18px] w-[18px] shrink-0 rounded-full border-[5px] border-accent bg-surface" />
    : <span className="inline-block h-[18px] w-[18px] shrink-0 rounded-full border-[1.5px] border-line-strong bg-surface" />
}

function WhoAtList() {
  const api = useRequestSheet()
  const { state } = api
  if (state.property.kind !== 'chosen') return null
  const place = state.property.property
  const chosenId = state.contact.kind === 'chosen' ? state.contact.contact.id : null
  const someoneNew = state.contact.kind === 'new'

  return (
    <div className="flex flex-col gap-2">
      {place.contacts.map((person) => {
        const on = person.id === chosenId
        const name = `${person.firstName} ${person.lastName}`
        return (
          <button
            key={person.id}
            type="button"
            role="radio"
            aria-checked={on}
            onClick={() => api.chooseContact(person.id, { propertyId: place.id, fromProperty: true })}
            className={cn('flex items-center gap-3 rounded-[12px] border-[1.5px] px-3.5 py-2.5 text-left',
              on ? 'border-accent bg-green-band-bg shadow-[0_0_0_3px_rgb(var(--color-accent-ring))]'
                : 'border-line bg-surface hover:border-line-strong')}
          >
            <Radio on={on} />
            <Avatar name={name} size={32} />
            {/* 16px from the name to the line under it, as drawn (Board 3 B). */}
            <span className="flex min-w-0 flex-1 flex-col leading-[15px]">
              <b className="truncate text-[13.5px] font-bold leading-[16px] text-text">{name}</b>
              {/* A phone has no room for the line beside the role, so it wraps rather than cutting a word. */}
              <span className={cn('text-[12px] text-text-muted', !api.phone && 'truncate')}>
                {knownContactLine(person, false)}
              </span>
            </span>
            <span className="whitespace-nowrap text-right text-[11.5px] text-text-faint">
              {person.isPrimary ? 'primary · bill-to' : person.role === 'bill_to' ? 'bill-to' : 'contact'}
            </span>
          </button>
        )
      })}
      {api.setup.canAddContacts && (
        <button
          type="button"
          role="radio"
          aria-checked={someoneNew}
          onClick={() => api.update((current) => ({
            ...current,
            contact: { kind: 'new', fields: EMPTY_TYPED_CONTACT },
            contactFromProperty: true,
          }))}
          className={cn('flex h-[38px] items-center gap-2.5 rounded-[12px] border-[1.5px] border-dashed px-3.5',
            'text-left text-[13px] font-semibold text-accent',
            someoneNew ? 'border-accent bg-green-band-bg' : 'border-accent-line')}
        >
          <PlusGlyph size={15} stroke={2.2} />
          Someone new at {place.name}
        </button>
      )}
    </div>
  )
}

// ---------------------------------------------------------------------------
// The section
// ---------------------------------------------------------------------------

export function ContactSection({ mode }: { mode: 'open' | 'card' }) {
  const api = useRequestSheet()
  const { state, phone } = api
  const contact = state.contact
  const atProperty = state.contactFromProperty && state.property.kind === 'chosen'
  const ready = contact.kind === 'chosen'
    || (contact.kind === 'new' && Boolean(contact.fields.firstName.trim() && contact.fields.lastName.trim()))

  const setFields = (fields: TypedContact) => api.update((current) => ({
    ...current, contact: { kind: 'new', fields },
  }))

  /* The folded card: typed → New + Edit; chosen → Change (Boards 3, 6). */
  if (mode === 'card' && contact.kind !== 'none') {
    const name = contact.kind === 'chosen'
      ? `${contact.contact.firstName} ${contact.contact.lastName}`
      : `${contact.fields.firstName} ${contact.fields.lastName}`.trim()
    const line = contact.kind === 'chosen' ? knownContactLine(contact.contact) : typedLine(contact.fields)
    return (
      <section data-section="contact">
        <SectionHeading section="contact" ready={ready} />
        <ContactCard name={name} line={line} isNew={contact.kind === 'new'}
          action={contact.kind === 'new' ? 'Edit' : 'Change'}
          onAction={() => (contact.kind === 'new' ? api.openSection('contact')
            : api.clearSection('contact'))} />
      </section>
    )
  }

  if (atProperty) {
    const place = state.property.kind === 'chosen' ? state.property.property : null
    return (
      <section data-section="contact">
        <SectionHeading section="contact" ready={ready} sub={`who at ${place?.name}?`} />
        <ReplaceAsk section="contact" />
        <WhoAtList />
        {contact.kind === 'new' && (
          <div className="mt-3">
            <ContactFields fields={contact.fields} onChange={setFields} phone={phone} />
          </div>
        )}
      </section>
    )
  }

  if (contact.kind === 'chosen') {
    return (
      <section data-section="contact">
        <SectionHeading section="contact" ready />
        <ReplaceAsk section="contact" />
        <ContactCard name={`${contact.contact.firstName} ${contact.contact.lastName}`}
          line={knownContactLine(contact.contact)} isNew={false} action="Change"
          onAction={() => api.clearSection('contact')} />
      </section>
    )
  }

  if (contact.kind === 'new') {
    return (
      <section data-section="contact">
        <SectionHeading section="contact" ready={ready} sub="new" />
        <ReplaceAsk section="contact" />
        <ContactFields fields={contact.fields} onChange={setFields} phone={phone} />
      </section>
    )
  }

  return (
    <section data-section="contact">
      <SectionHeading section="contact" ready={false}
        sub={phone ? undefined : 'find them in the search above — most people are already here'} />
      <ReplaceAsk section="contact" />
      {api.setup.canAddContacts
        ? (
          <div className="flex items-center gap-2.5">
            {/* `!`: the kit's 12px is in the same class list and `cn` does not merge (Board 2: 12.5). */}
            <Button className={cn('gap-1.5 !px-3.5', phone ? '!h-11 !text-[14px]' : '!h-[34px] !text-[12.5px]')}
              onClick={() => { setFields(EMPTY_TYPED_CONTACT); api.requestFocus('firstName') }}>
              <PlusGlyph size={14.5} stroke={2.2} />
              Add a new contact
            </Button>
            {!phone && <span className="text-[12px] text-text-faint">first and last name, a phone, an email</span>}
          </div>
        )
        : <span className="text-[12px] text-text-muted">You can find people here but not add them.</span>}
      <MissingHint text={api.hintFor('contact')} />
    </section>
  )
}

/** The contact the sheet holds, as a known one, for the sections that need its facts. */
export function knownOf(state: SheetState): KnownContact | null {
  return state.contact.kind === 'chosen' ? state.contact.contact : null
}
